import { Directive, ElementRef, Renderer2, Input, AfterViewChecked, Inject, DOCUMENT } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { BrowserService } from '@core/browser.service';

@Directive({
  selector: '[zbpNgSelectSrValue]',
  standalone: true
})
export class NgSelectSrValueDirective implements AfterViewChecked {

  @Input() zbpNgSelectSrValue: AbstractControl | null = null;
  /** If provided and control is touched & invalid, will update the aria-describedby to use the error message id. */
  @Input() zbpNgSelectErrorDescribedById: string = '';
  @Input() options: any[] = [];
  @Input() bindLabel: string = 'label';
  @Input() bindValue: string = 'value';
  @Input() labelForId: string = 'Select option';

  private retryCount = 0;
  private maxRetries = 20;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private browserService: BrowserService,
    @Inject(DOCUMENT) private document: Document
  ) { }

  ngAfterViewChecked(): void {
    const host = this.el.nativeElement;
    this.trySetupAccessibility(host);
  }

  private trySetupAccessibility(host: HTMLElement): void {
    // Check if ng-select has rendered its internal combobox input
    const input = host.querySelector('input[role="combobox"]') as HTMLInputElement;

    if (!input && this.retryCount < this.maxRetries) {
      this.retryCount++;
      setTimeout(() => this.trySetupAccessibility(host), 100);
      return;
    }

    if (input) {
      this.setupAriaLabel(host, input);
    }
  }

  /** Sets up the aria-label for the ng-select component that screen readers will use. */
  private setupAriaLabel(host: HTMLElement, input: HTMLInputElement): void {
    const updateAriaLabel = () => {
      let labelText = this.labelForId;
      const labelElement = this.document.querySelector(`label[for="${labelText}"]`) as HTMLElement;
      const inputWrapper = host.querySelector('.ng-input') as HTMLDivElement;

      if (!labelElement) {
        // If there isn't a `labelForId` provided, attempt to find a nearby label to use for the aria-label.
        // This supports forms that use visual labels without proper for/id associations.
        const label = host.closest('.form-group')?.querySelector('label')
          || host.previousElementSibling;
        labelText = (label as HTMLElement)?.textContent?.trim() || 'Select option';
      } else {
        // Use the provided labelForId to find the associated label text
        labelText = labelElement?.textContent?.trim() || 'Select option';
      }

      // Get selected value text
      const selectedValueText = this.getSelectedValueText(host);
      const fullLabel = selectedValueText ? `${labelText}: ${selectedValueText}` : labelText;
      this.renderer.removeAttribute(input, 'aria-labelledby');
      this.renderer.setAttribute(input, 'aria-label', fullLabel);
      this.renderer.setAttribute(input, 'aria-readonly', 'true');
      // Remove verbose aria attributes that cause unwanted announcements
      this.renderer.removeAttribute(input, 'aria-autocomplete');

      //Update the validity if invalid & error message id provided
      if (this.zbpNgSelectErrorDescribedById) {
        const isInvalid = this.zbpNgSelectSrValue?.invalid && this.zbpNgSelectSrValue?.touched;
        if (isInvalid) {
          input.setAttribute('aria-describedby', this.zbpNgSelectErrorDescribedById);
        } else if (input.getAttribute('aria-describedby')) {
          input.removeAttribute('aria-describedby');
        }
      }

      // Adding specific behavior to support NVDA virtual cursor navigation
      if (this.browserService.isChrome || this.browserService.isFireFox) {
        const isDisabled = input.getAttribute('aria-disabled');

        if (isDisabled === 'true') {
          inputWrapper.setAttribute('aria-disabled', 'true');
        }

        inputWrapper.setAttribute('aria-expanded', input.getAttribute('aria-expanded'));
        inputWrapper.setAttribute('role', 'combobox');
        inputWrapper.setAttribute('aria-label', fullLabel);
      }
    };

    // Initial setup
    updateAriaLabel();

    // Store for later use
    const hostElement = host as any;
    hostElement._updateAriaLabel = updateAriaLabel;
  }

  private getSelectedValueText(host: HTMLElement): string {
    // Get the displayed value from ng-select
    const valueElement = host.querySelector('.ng-value-label');
    if (valueElement && valueElement.textContent?.trim()) {
      return valueElement.textContent.trim();
    }

    const ngValue = host.querySelector('.ng-value');
    if (ngValue && ngValue.textContent?.trim()) {
      return ngValue.textContent.trim();
    }

    // Check placeholder
    const placeholder = host.querySelector('.ng-placeholder');
    if (placeholder && placeholder.textContent?.trim()) {
      return placeholder.textContent.trim();
    }

    return '';
  }
}
